import moment from "moment";
import { faArrowRotateBack, faEye } from "@fortawesome/pro-solid-svg-icons";
import { IconButton } from "../../../form/Button";
import { VersionHistoryPreviewModal } from "./VersionHistoryPreviewModal";
import { useModal } from "../../../layout/ModalProvider";
import { RevertVersionModal } from "./RevertVersionModal";
import { DocPageVersion, DocUser } from "../DocumentPageHeader";
import { FC } from "react";
import { ConfigInterface } from "../../../../config";

export const VersionHistoryModal: FC<{
  config: ConfigInterface;
  versions: DocPageVersion[];
  users: DocUser[];
  readonly: boolean;
  onFinished: () => void;
  onPageReverted: (version: DocPageVersion) => void;
}> = (props) => {
  return (
    <div className={"py-2 mb-4"}>
      <div className={"rounded-lg border border-slate-200 bg-white"}>
        <table className={"w-full text-sm"}>
          <thead className={"bg-slate-50 text-slate-600 uppercase text-xs tracking-wider"}>
            <tr>
              <th className={"px-4 py-3 text-left font-medium"}>Versie</th>
              <th className={"px-4 py-3 text-left font-medium"}>Aangepast op</th>
              <th className={"px-4 py-3 text-left font-medium w-full"}>Aangepast door</th>
              <th className={"px-4 py-3 text-left font-medium"}>Acties</th>
            </tr>
          </thead>

          <tbody className={"divide-y divide-slate-100"}>
            {[...(props.versions ?? [])]
              .sort((a, b) => b.version - a.version)
              .map((v, index) => {
                return (
                  <TableRow
                    config={props.config}
                    version={v}
                    user={props.users.find((u) => u.id === v.editedBy)}
                    readonly={props.readonly}
                    onFinished={props.onFinished}
                    onPageReverted={props.onPageReverted}
                    key={index}
                  />
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TableRow: FC<{
  config: ConfigInterface;
  version: DocPageVersion;
  user: DocUser | undefined;
  readonly: boolean;
  onFinished: () => void;
  onPageReverted: (version: DocPageVersion) => void;
}> = (props) => {
  const previewModal = useModal({
    title: `Versie ${props.version.version} preview`,
    body: <VersionHistoryPreviewModal version={props.version} />,
    size: "2xl",
  });
  const revertModal = useModal({
    title: `Versie ${props.version.version} terugzetten`,
    body: <RevertVersionModal config={props.config} version={props.version} onPageReverted={props.onPageReverted} />,
    size: "md",
  });

  return (
    <tr className={"hover:bg-slate-50 transition-colors"}>
      <td className={"px-4 py-3 font-medium text-slate-900 whitespace-nowrap"}>
        v
        {props.version.isCurrentVersion ? (
          <>
            {props.version.version} <span className={"text-brand-primary"}>(huidig)</span>
          </>
        ) : (
          props.version.version
        )}
      </td>

      <td className={"px-4 py-3 text-slate-600 whitespace-nowrap"}>
        {moment(props.version.createdAt).format("DD-MM-YYYY - HH:mm")}
      </td>

      <td>
        <div className={"px-4 flex items-center space-x-2"}>
          <div
            className={
              "flex h-7 w-7 pt-[2px] text-white font-medium text-xs rounded-full bg-brand-primary items-center justify-center text-center"
            }
          >
            {props.user?.name
              .split(" ")
              .map((part) => part[0])
              .join("")
              .toUpperCase()}
          </div>
          <div className={"text-base"}>{props.user?.name ?? props.version.editedBy}</div>
        </div>
      </td>

      <td className={"px-4 py-3 text-slate-500"}>
        <div className={"flex flex-row space-x-2"}>
          <IconButton icon={faEye} size={"xs"} type={"secondary"} onClick={() => previewModal.open()} />
          {!props.readonly && (
            <IconButton
              icon={faArrowRotateBack}
              size={"xs"}
              type={"secondary"}
              disabled={props.version.isCurrentVersion}
              onClick={() => revertModal.open()}
            />
          )}
        </div>
      </td>
    </tr>
  );
};

